import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { usePages } from "../translations/pages";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/Footer";

const TermsOfUse = () => {
  const { language } = useLanguage();
  const tp = { ...usePages(language).legal, back: usePages(language).back };
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <Navbar isScrolled={true} />
      <div className="wrap-cayus pt-28 pb-16">
        {/* Back link */}
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-[#5E6C83] hover:text-[#1B1B1B] mb-10 transition-colors"
          data-testid="terms-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          {tp.back}
        </a>

        <div className="rule-cobalt mb-5" />

        <p className="mono-cayus text-[#0047AB] mb-4">{tp.termsEyebrow}</p>

        <h1 className="text-3xl sm:text-4xl font-bold text-[#1B1B1B] tracking-tight mb-2">
          Terms of Use
        </h1>
        <p className="text-sm text-[#5E6C83] mb-10">Cayus Limited. Last updated: March 2026</p>

        <p className="text-sm text-[#5E6C83] bg-[#F7F9FC] border border-[#C3CFE0] rounded-lg px-4 py-3 mb-8 max-w-[70ch]">{tp.refNote}</p>
        <div className="prose-cayus max-w-[70ch]">

          {/* 1. Purpose */}
          <h2>1. Purpose</h2>
          <p>
            These Terms of Use govern access to and use of the website <strong>cayus.ai</strong> (the "Website"), operated by Cayus Limited.
          </p>
          <p>
            The Website is an informational site designed to present the Cayus software, its services, and enable businesses to contact Cayus for previews, demos, and commercial discussions.
          </p>
          <p>
            <em>These terms do not govern the use of the Cayus SaaS platform, which is subject to a separate agreement.</em>
          </p>

          {/* 2. Operator */}
          <h2>2. Website Operator</h2>
          <p>
            <strong>Cayus Limited</strong><br />
            Private limited company registered in England &amp; Wales<br />
            Company number: 17046973<br />
            Registered office: 15 Lea Road, Sevenoaks, TN13 1QP, United Kingdom<br />
            Email: <a href="mailto:privacy@cayus.ai" className="text-[#1d4ed8] hover:underline">privacy@cayus.ai</a>
          </p>

          {/* 3. Access */}
          <h2>3. Access to the Website</h2>
          <p>
            The Website is accessible free of charge to any user with internet access. Cayus Limited makes every effort to maintain the availability of the Website but does not guarantee uninterrupted or error-free access.
          </p>
          <p>
            Cayus Limited reserves the right to suspend, restrict, or discontinue all or part of the Website at any time, without prior notice, for maintenance, updates, or any other reason.
          </p>

          {/* 4. Intellectual Property */}
          <h2>4. Intellectual Property</h2>
          <p>
            All content published on the Website, including but not limited to text, graphics, logos, icons, images, videos, software, and underlying code, is the exclusive property of Cayus Limited or its licensors and is protected by applicable intellectual property laws.
          </p>
          <p>
            No part of the Website may be reproduced, distributed, modified, transmitted, or used in any form without the prior written consent of Cayus Limited. This includes:
          </p>
          <ul>
            <li>The Cayus name, logo, and branding</li>
            <li>All textual and visual content</li>
            <li>The website design, layout, and structure</li>
            <li>Software, algorithms, and proprietary frameworks (including the Cayus Behaviour Ladders and level model)</li>
          </ul>

          {/* 5. Forms */}
          <h2>5. Contact Forms and Submissions</h2>
          <p>
            The Website includes forms that allow users to submit enquiries, request demos, or apply for pilot programmes. By submitting information through these forms, you:
          </p>
          <ul>
            <li>Confirm that the information provided is accurate and complete</li>
            <li>Acknowledge that Cayus Limited will process this information in accordance with its <a href="/privacy" className="text-[#1d4ed8] hover:underline">Privacy Policy</a></li>
            <li>Understand that submitting a form does not constitute a contractual agreement</li>
          </ul>
          <p>
            Cayus Limited undertakes to handle all submissions with confidentiality and to use the data solely for the purposes described in the Privacy Policy.
          </p>

          {/* 6. User Conduct */}
          <h2>6. User Conduct</h2>
          <p>When using the Website, you agree not to:</p>
          <ul>
            <li>Use the Website for any unlawful purpose or in violation of any applicable laws</li>
            <li>Attempt to gain unauthorised access to the Website's systems or networks</li>
            <li>Introduce viruses, malware, or any other harmful code</li>
            <li>Collect or harvest personal data of other users</li>
            <li>Interfere with the proper functioning of the Website</li>
            <li>Use automated tools (bots, scrapers) to access or extract content without prior authorisation</li>
          </ul>

          {/* 7. Limitation of Liability */}
          <h2>7. Limitation of Liability</h2>
          <p>
            The information provided on the Website is for general informational purposes only. Cayus Limited makes no representations or warranties of any kind regarding the accuracy, completeness, or suitability of the information.
          </p>
          <p>To the fullest extent permitted by law, Cayus Limited shall not be liable for:</p>
          <ul>
            <li>Any interruption or unavailability of the Website</li>
            <li>Any technical errors, bugs, or malfunctions</li>
            <li>Any loss of data resulting from use of the Website</li>
            <li>Any damage caused by third-party actions, viruses, or network issues</li>
            <li>Any decision made based on information found on the Website</li>
          </ul>

          {/* 8. External Links */}
          <h2>8. External Links</h2>
          <p>
            The Website may contain links to third-party websites. These links are provided for convenience and informational purposes only. Cayus Limited has no control over the content, policies, or practices of third-party websites and accepts no responsibility for them.
          </p>
          <p>
            Accessing external websites is at your own risk. We encourage you to review the terms and privacy policies of any third-party site you visit.
          </p>

          {/* 9. Cookies */}
          <h2>9. Cookies and Analytics</h2>
          <p>
            The Website uses cookies as described in our <a href="/privacy" className="text-[#1d4ed8] hover:underline">Privacy Policy</a>. By continuing to use the Website after being presented with the cookie banner, you acknowledge our use of essential cookies. Analytics cookies are only activated after your explicit consent.
          </p>

          {/* 10. Modifications */}
          <h2>10. Modifications to These Terms</h2>
          <p>
            Cayus Limited reserves the right to modify these Terms of Use at any time. Updated terms will be published on this page with a revised "Last Updated" date. Continued use of the Website following any changes constitutes acceptance of the revised terms.
          </p>

          {/* 11. Governing Law */}
          <h2>11. Governing Law and Jurisdiction</h2>
          <p>
            These Terms of Use are governed by and construed in accordance with the laws of England and Wales. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>

          {/* 12. Contact */}
          <h2>12. Contact</h2>
          <p>For any questions regarding these Terms of Use:</p>
          <p>
            <strong>Cayus Limited</strong><br />
            15 Lea Road, Sevenoaks<br />
            TN13 1QP<br />
            United Kingdom<br />
            Email: <a href="mailto:privacy@cayus.ai" className="text-[#1d4ed8] hover:underline">privacy@cayus.ai</a>
          </p>
        </div>
      </div>
    <SiteFooter />
    </div>
  );
};

export default TermsOfUse;
